import { PlayerManager } from 'chromecast-caf-receiver/cast.framework';
import {
  ContentProtection,
  PlaybackConfig,
} from 'chromecast-caf-receiver/cast.framework';
import {
  LoadRequestData,
  StreamType,
} from 'chromecast-caf-receiver/cast.framework.messages';
import { NetworkRequestInfo } from 'chromecast-caf-receiver/cast.framework';
import type { CastSession } from '../types/session';
import type { CastMedia } from '../types/media';
import { Logger } from '../logger';

/**
 * DRM / Widevine handler — configures license acquisition for protected content.
 *
 * Ported from `legacy/js/receiver.js` lines 103-137. Responsible for:
 * - Bypassing DRM configuration for LIVE streams
 * - Setting up the Widevine license request (encoding the challenge as Base64,
 *   building the GraphQL body for Hermes `drm_license_v_2`)
 * - Processing the license response (extracting the Base64 license and decoding)
 *
 * The handler reads `session.userToken` (set by `user-channel.ts`) for
 * authorization, and `media.mediaId` (set by `load-interceptor.ts`) to
 * identify the media whose license key Hermes should resolve.
 *
 * **No shaka-player dependency** — all Base64/UTF-8 encoding uses native
 * browser APIs (`btoa`, `atob`, `TextDecoder`).
 */

const LOG_TAG = 'DRM';

/** Hermes GraphQL endpoint — same URL used as the CAF license URL. */
const HERMES_API_URL = 'https://hermes.brasilparalelo.com.br/api';

/** DRM technology for DASH/`mpd` playback (Hermes maps `mpd` → `widevine`). */
const DRM_TYPE = 'widevine';

/**
 * The raw GraphQL query string for `drm_license_v_2`.
 *
 * We inline the query rather than importing `GetDrmLicenseV2Document` from
 * the codegen output because the CAF `licenseRequestHandler` constructs
 * the HTTP body itself (as a JSON string set on `requestInfo.content`).
 * The generated SDK method (`Hermes.client.GetDRMLicenseV2`) cannot be
 * used here because the CAF controls the entire HTTP request lifecycle.
 *
 * Keep in sync with `graphql/getDrmLicense.graphql`.
 */
const DRM_LICENSE_QUERY = `query GetDRMLicenseV2($mediaId: String!, $drmType: String!, $licenseChallenge: String!) {
  drm_license_v_2(media_id: $mediaId, drm_type: $drmType, license_challenge: $licenseChallenge) {
    ... on license {
      license
    }
    ... on error {
      message
    }
  }
}`;

// ---------------------------------------------------------------------------
// Base64 / UTF-8 helpers (replacing shaka.util)
// ---------------------------------------------------------------------------

/**
 * Encodes a `Uint8Array` to a standard Base64 string.
 * Replaces `shaka.util.Uint8ArrayUtils.toStandardBase64()`.
 */
function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

/**
 * Decodes a standard Base64 string to a `Uint8Array`.
 * Replaces `shaka.util.Uint8ArrayUtils.fromBase64()`.
 */
function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// ---------------------------------------------------------------------------
// GraphQL body builder
// ---------------------------------------------------------------------------

/**
 * Builds the JSON body for the Hermes `drm_license_v_2` GraphQL query,
 * producing a standard `{ query, variables }` GraphQL payload.
 */
function buildDrmLicenseBody(
  mediaId: string,
  licenseChallenge: string,
): string {
  return JSON.stringify({
    query: DRM_LICENSE_QUERY,
    variables: {
      mediaId,
      drmType: DRM_TYPE,
      licenseChallenge,
    },
  });
}

// ---------------------------------------------------------------------------
// Hermes DRM license response shape
// ---------------------------------------------------------------------------

/**
 * Shape of the Hermes GraphQL response when the CAF fetches the license.
 *
 * The response is a raw JSON body (not parsed by `graphql-request`), so
 * we type it manually to match the `GetDrmLicenseV2Query` union.
 */
interface HermesDrmResponse {
  data: {
    drm_license_v_2:
      | { license: string }
      | { message: string };
  };
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

/**
 * Registers the media playback info handler that configures DRM/Widevine
 * license acquisition on the given player manager.
 *
 * @param playerManager - Cast player manager instance
 * @param session       - Session state (provides `userToken` for authorization)
 * @param media         - Media state (provides `mediaId` for DRM)
 */
export function registerDrmHandler(
  playerManager: PlayerManager,
  session: CastSession,
  media: CastMedia,
): void {
  playerManager.setMediaPlaybackInfoHandler(
    (
      loadRequest: LoadRequestData,
      playbackConfig: PlaybackConfig,
    ): PlaybackConfig => {
      // ---------------------------------------------------------------
      // 1. LIVE bypass — no DRM for live streams
      // ---------------------------------------------------------------
      try {
        if (
          loadRequest.media &&
          loadRequest.media.streamType === StreamType.LIVE
        ) {
          Logger.info(LOG_TAG, 'LIVE bypass — skipping DRM configuration', {
            contentId: loadRequest.media.contentId,
          });
          return playbackConfig;
        }
      } catch (e) {
        // Defensive: if streamType check throws, fall through to DRM config
        Logger.warn(LOG_TAG, 'Error checking stream type', e);
      }

      // ---------------------------------------------------------------
      // 2. Nothing DRM-protected to configure — skip
      // ---------------------------------------------------------------
      // Live content is never Widevine-protected. The `streamType` bypass
      // above only catches an *active* live; once it ends it is loaded as a
      // BUFFERED replay, so it is filtered here by playlist type instead.
      if (!media.mediaId || media.playlistType === 'live') {
        Logger.debug(LOG_TAG, 'No protected media — skipping DRM configuration', {
          mediaId: media.mediaId,
          playlistType: media.playlistType,
        });
        return playbackConfig;
      }

      // ---------------------------------------------------------------
      // 3. Configure Widevine protection
      // ---------------------------------------------------------------
      playbackConfig.protectionSystem = ContentProtection.WIDEVINE;
      playbackConfig.licenseUrl = HERMES_API_URL;

      Logger.debug(LOG_TAG, 'Widevine DRM configured', {
        licenseUrl: HERMES_API_URL,
        hasUserToken: !!session.userToken,
        mediaId: media.mediaId,
      });

      // ---------------------------------------------------------------
      // 4. License request handler — encode challenge and build body
      // ---------------------------------------------------------------
      playbackConfig.licenseRequestHandler = (
        requestInfo: NetworkRequestInfo,
      ): void => {
        requestInfo.withCredentials = false;
        requestInfo.headers = {
          'content-type': 'application/json',
          Authorization: `Bearer ${session.userToken}`,
        };

        // Encode the Widevine challenge from Uint8Array to Base64
        const challengeBytes = requestInfo.content ?? new Uint8Array(0);
        const licenseChallenge = uint8ArrayToBase64(challengeBytes);

        // Build the GraphQL request body and encode to Uint8Array
        const bodyString = buildDrmLicenseBody(
          media.mediaId,
          licenseChallenge,
        );
        const encoder = new TextEncoder();
        requestInfo.content = encoder.encode(bodyString);

        Logger.debug(LOG_TAG, 'License request prepared', {
          challengeLength: challengeBytes.length,
        });
      };

      // ---------------------------------------------------------------
      // 5. License handler — extract and decode the license from response
      // ---------------------------------------------------------------
      playbackConfig.licenseHandler = (response: Uint8Array): Uint8Array => {
        // Decode the raw response bytes to a UTF-8 string
        const decoder = new TextDecoder('utf-8');
        const responseText = decoder.decode(response);

        // Parse the Hermes GraphQL response
        const jsonResponse = JSON.parse(responseText) as HermesDrmResponse;
        const drmResult = jsonResponse.data.drm_license_v_2;

        // Check for error response
        if ('message' in drmResult) {
          Logger.error(LOG_TAG, 'Hermes DRM license error', {
            message: drmResult.message,
          });
          throw new Error(`DRM license error: ${drmResult.message}`);
        }

        // Decode the Base64 license to Uint8Array
        const licenseBytes = base64ToUint8Array(drmResult.license);

        Logger.debug(LOG_TAG, 'License received and decoded', {
          licenseLength: licenseBytes.length,
        });

        return licenseBytes;
      };

      return playbackConfig;
    },
  );
}
